import { openDB } from "idb";
import api from "./api";

const DB_NAME = "LipCenterDB";
const STORE_NAME = "lipids";

const initializeDB = async () => {
  const db = await openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, {
          keyPath: "id",
          autoIncrement: true,
        });
      }
    },
  });
  return db;
};

/**
 * Сохраняет данные в локальную базу
 */
export const saveToLocalDB = async (key: string, data: any) => {
  const db = await initializeDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  await tx.objectStore(STORE_NAME).put({ id: key, data });
  await tx.done;
};

/**
 * Загружает данные из локальной базы
 */
export const loadFromLocalDB = async (key: string) => {
  const db = await initializeDB();
  const tx = db.transaction(STORE_NAME, "readonly");
  const record = await tx.objectStore(STORE_NAME).get(key);
  return record ? record.data : null;
};

/**
 * Загружает данные из API и синхронизирует с локальной базой
 */
export const syncLipidsData = async () => {
  try {
    const response = await api.get("/lipids/my_lipids/info_cookie");

    // Если есть данные, сохраняем их
    if (response.data && response.data.length > 0) {
      await saveToLocalDB("lipids", response.data);

      // Сохраняем последнюю запись липидного профиля в localStorage для расчета рисков
      const latestLipid = response.data.sort(
        (a: any, b: any) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];

      if (latestLipid) {
        const lipidData = {
          total_cholesterol: latestLipid.total_cholesterol,
          hdl: latestLipid.hdl,
        };
        localStorage.setItem("lipid_data", JSON.stringify(lipidData));
      }

      console.log("Липидограммы синхронизированы.");
      return response.data;
    }

    return null;
  } catch (error: any) {
    // Обрабатываем случай отсутствия данных (404)
    if (error.response?.status === 404) {
      console.log("У пользователя нет данных липидограмм");
      // Очищаем локальные данные, если они были
      await deleteFromLocalDB("lipids");
      return null;
    }

    console.error("Ошибка при синхронизации липидограмм:", error);
    throw new Error("Не удалось синхронизировать данные липидограмм.");
  }
};

/**
 * Получает липидограммы (сначала из локальной базы, затем из API при необходимости)
 */
export const getLipids = async () => {
  try {
    const localData = await loadFromLocalDB("lipids");
    if (localData) {
      console.log("Данные загружены из локальной базы.");
      return localData;
    }

    console.log("Данные отсутствуют в локальной базе. Синхронизация...");
    const syncedData = await syncLipidsData();

    if (!syncedData) {
      console.log("Данные липидограмм отсутствуют");
      return null;
    }

    return syncedData;
  } catch (error) {
    console.error("Ошибка при получении липидограмм:", error);
    throw new Error("Не удалось получить данные липидограмм.");
  }
};

/**
 * Удаляет запись из локальной базы данных по ключу
 */
export const deleteFromLocalDB = async (key: string) => {
  try {
    const db = await initializeDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    await tx.objectStore(STORE_NAME).delete(key);
    await tx.done;
    console.log(`Запись с ключом ${key} успешно удалена из локальной базы.`);
  } catch (error) {
    console.error(`Ошибка при удалении записи с ключом ${key}:`, error);
    throw new Error("Не удалось удалить запись из локальной базы.");
  }
};
/**
 * Получает данные последней липидограммы
 */
export const clearAll = async () => {
  const db = await initializeDB();
  await db.clear(STORE_NAME);
};
